import type { DragEvent } from 'react';
import type { WebsiteEventGoal } from 'types';
import type { EventGoalRole } from '@/state/useWebsiteDetails';
import { useState } from 'react';
import { useDebounce } from 'hooks/useDebounce';
import { useWebsiteDetails } from '@/state/useWebsiteDetails';

/**
 * Same normalisation every DataTable search in the product uses — accent-insensitive and
 * case-insensitive — so "Añadir" is found by typing "anadir". It is repeated here rather
 * than imported because useDataControl keeps its copy private.
 */
const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * How many goals of one role are put in the DOM at once.
 *
 * Real websites carry thirteen thousand event goals, and every rendered one is a badge
 * with its own dropdown trigger. Rendering a whole role is what pushed the modal past the
 * viewport and made its buttons unreachable, so a role renders at most this many and the
 * UI says how many it is hiding — the cap is never silent.
 */
export const RENDER_LIMIT = 200;

/** Long enough that typing a word costs one filter pass over the full list, not eight. */
const SEARCH_DEBOUNCE_MS = 200;

export type EventGoalsBucket = {
  /** The goals actually rendered — never more than RENDER_LIMIT. */
  goals: WebsiteEventGoal[];
  /** How many goals of this role match the current search. */
  matchCount: number;
  /** How many goals of this role exist, search ignored. */
  totalCount: number;
  isCapped: boolean;
};

export type EventGoalsBuckets = Record<EventGoalRole, EventGoalsBucket>;

type BucketEventGoalsParams = {
  goals: readonly WebsiteEventGoal[];
  /** Already trimmed and normalized. Empty means "no search". */
  query: string;
};

const emptyBucket = (): EventGoalsBucket => ({ goals: [], matchCount: 0, totalCount: 0, isCapped: false });

/**
 * One pass over every goal: split by role, count matches, keep only the first
 * RENDER_LIMIT of each. Filtering three roles separately would walk the list three
 * times, and slicing after filtering would build three arrays of thousands of items
 * only to throw them away.
 */
export function bucketEventGoals({ goals, query }: BucketEventGoalsParams): EventGoalsBuckets {
  const buckets: EventGoalsBuckets = { primary: emptyBucket(), secondary: emptyBucket(), ignored: emptyBucket() };

  for (const goal of goals) {
    const bucket = buckets[goal.role];
    if (!bucket) continue;

    bucket.totalCount += 1;
    if (query && !normalize(goal.name ?? '').includes(query)) continue;

    bucket.matchCount += 1;
    if (bucket.goals.length < RENDER_LIMIT) bucket.goals.push(goal);
    else bucket.isCapped = true;
  }

  return buckets;
}

export const useEventGoalsMapping = (websiteId: number) => {
  const { eventGoals, isLoading, updateEventGoalRole } = useWebsiteDetails(websiteId);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dragOverRole, setDragOverRole] = useState<EventGoalRole | null>(null);

  // The input stays instant; the thirteen-thousand-item filter runs once you stop typing.
  useDebounce(() => setSearchQuery(searchInput), SEARCH_DEBOUNCE_MS, [searchInput]);

  const goals = eventGoals ?? [];
  const query = normalize(searchQuery.trim());
  const buckets = bucketEventGoals({ goals, query });

  const handleDragStart = (ev: DragEvent, goal: WebsiteEventGoal) => {
    ev.dataTransfer.setData('goalId', String(goal.id));
    ev.dataTransfer.setData('currentRole', goal.role);
    ev.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (ev: DragEvent) => {
    ev.preventDefault();
    ev.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (ev: DragEvent) => {
    const relatedTarget = ev.relatedTarget as HTMLElement | null;
    if (!ev.currentTarget.contains(relatedTarget)) setDragOverRole(null);
  };

  const handleDrop = async (ev: DragEvent, targetRole: EventGoalRole) => {
    ev.preventDefault();
    setDragOverRole(null);

    const goalId = +ev.dataTransfer.getData('goalId');
    const currentRole = ev.dataTransfer.getData('currentRole');
    if (currentRole === targetRole) return;

    await updateEventGoalRole(goalId, targetRole);
  };

  return {
    isLoading,
    buckets,
    totalCount: goals.length,
    searchInput,
    setSearchInput,
    isSearching: query.length > 0,
    dragOverRole,
    setDragOverRole,
    updateEventGoalRole,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
};
